// Trip status values and lifecycle gates. Lives in @repo/constants so both
// the backend (via @repo/db) and the frontend can import without pulling in
// the database layer.

export const TRIP_STATUSES = [
  'draft',
  'submitted',
  'waiting_approval',
  'approved',
  'active',
  'interrupted',
  'undelivered',
  'rejected',
  'needs_correction',
  'completed',
  'cancelled',
] as const
export type TripStatus = (typeof TRIP_STATUSES)[number]

// Trip statuses considered "done" from the dispatch's point of view: the
// driver has handed the data off for review or the trip has reached a
// terminal state. Source of truth for dispatch-completion gates and the
// cancel-affordance gate on the order trip-history view.
export const TRIP_DONE_STATUSES = [
  'submitted',
  'waiting_approval',
  'approved',
  'rejected',
  'completed',
  'cancelled',
  'undelivered',
] as const satisfies readonly TripStatus[]

export function isTripDoneStatus(status: TripStatus): boolean {
  return (TRIP_DONE_STATUSES as readonly TripStatus[]).includes(status)
}

// Trips that never count as a delivered load, whatever their delivery ticket
// says: the other half of the load counter, beside the ticket statuses in
// `TICKET_NON_COUNTING_STATUSES` (`TKT-04`).
export const TRIP_NON_COUNTING_STATUSES = [
  'cancelled',
  'rejected',
  'undelivered',
] as const satisfies readonly TripStatus[]

// A load counts as delivered once its trip is handed off and not undone
// (`TKT-04`, `TRP-08`): the one list the driver's rings read, server and app.
export const TRIP_DELIVERED_STATUSES = TRIP_DONE_STATUSES.filter(
  (status) => !(TRIP_NON_COUNTING_STATUSES as readonly TripStatus[]).includes(status),
)

export function isTripDeliveredStatus(status: string): boolean {
  return (TRIP_DELIVERED_STATUSES as readonly string[]).includes(status)
}

// GEO-35: how long a shared replay link stays open. Long enough to settle a
// dispute, never for good.
export const TRIP_ROUTE_SHARE_DAYS = [7, 30, 90] as const
export type TripRouteShareDays = (typeof TRIP_ROUTE_SHARE_DAYS)[number]
